import { mediaQueryLarge, requestIdleCallback, startViewTransition, getActiveCollectionGrid } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  // Declare private fields at class level
  #gridChangeHandler;
  #gridClickHandler;
  #gridControlsObserver;
  #filterUpdateRestoreHandler;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    this.#filterUpdateRestoreHandler = () => {
      requestAnimationFrame(() => {
        this.#sanitizeGridViewForViewport();
        this.#restoreGridView();
      });
    };
    document.addEventListener(ThemeEvents.FilterUpdate, this.#filterUpdateRestoreHandler);

    // Use document-level event delegation since grid controls may be outside results-list
    // Listen for change events on grid controls anywhere in the document
    this.#gridChangeHandler = (event) => {
      if (!this.isConnected) return;

      const target = event.target;
      
      if (target instanceof HTMLInputElement && (target.name === 'grid-mobile' || target.name === 'grid')) {
        // Only handle if this results-list contains the product grid
        const grid = this.#getGrid();
        if (grid) {
          event.stopPropagation(); // Prevent duplicate handling
          this.updateLayout(event);
        }
      }
    };
    
    // Listen for click events on grid controls (fallback for touch devices)
    this.#gridClickHandler = (event) => {
      if (!this.isConnected) return;

      const target = event.target;
      
      // Check if click is on a grid control input, label, icon, or SVG
      const label = target.closest('label.column-options__option');
      let gridInput = null;
      
      if (label) {
        gridInput = label.querySelector('input[type="radio"][name="grid"], input[type="radio"][name="grid-mobile"]');
      } else if (target instanceof HTMLInputElement && (target.name === 'grid-mobile' || target.name === 'grid')) {
        gridInput = target;
      } else if (target.closest('.column-options-wrapper')) {
        // Click might be on wrapper, find the closest input
        const wrapper = target.closest('.column-options-wrapper');
        const clickedOption = target.closest('.column-options__option') || 
          (target.closest('.column-picker')?.parentElement);
        if (clickedOption) {
          gridInput = clickedOption.querySelector('input[type="radio"][name="grid"], input[type="radio"][name="grid-mobile"]');
        }
      }
      
      if (gridInput instanceof HTMLInputElement) {
        const grid = this.#getGrid();
        if (grid) {
          event.stopPropagation();

          const name = gridInput.name;
          const value = gridInput.value;
          this.#updateRadioButtonStates(name, value);

          gridInput.checked = true;

          const changeEvent = new Event('change', { bubbles: true, cancelable: true });
          gridInput.dispatchEvent(changeEvent);
        }
      }
    };
    
    // Attach document-level listeners
    document.addEventListener('change', this.#gridChangeHandler, true);
    document.addEventListener('click', this.#gridClickHandler, true);
    
    // Also attach listeners directly to existing grid controls
    this.#attachGridControlListeners();
    
    // Restore grid view from sessionStorage after re-render
    this.#sanitizeGridViewForViewport();
    this.#restoreGridView();
    
    // Listen for section load events to restore grid view after section morphs
    document.addEventListener('shopify:section:load', this.#handleSectionLoad);
    
    // Watch for dynamically added grid controls (after section re-renders)
    this.#watchForGridControls();
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    document.removeEventListener('shopify:section:load', this.#handleSectionLoad);
    if (this.#filterUpdateRestoreHandler) {
      document.removeEventListener(ThemeEvents.FilterUpdate, this.#filterUpdateRestoreHandler);
    }
    
    // Remove document-level event listeners
    if (this.#gridChangeHandler) {
      document.removeEventListener('change', this.#gridChangeHandler, true);
    }
    if (this.#gridClickHandler) {
      document.removeEventListener('click', this.#gridClickHandler, true);
    }
    
    // Disconnect MutationObserver if it exists
    if (this.#gridControlsObserver) {
      this.#gridControlsObserver.disconnect();
      this.#gridControlsObserver = null;
    }
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) {
      return;
    }

    // Support both desktop and mobile grid controls
    let value = target.value;
    if (target.name === 'grid-mobile') {
      // For mobile, set the product-grid-view to 'mobile-single' or 'default'
      // Update all radio buttons in the same group to ensure correct visual state
      this.#updateRadioButtonStates('grid-mobile', value);
      this.#animateLayoutChange(value);
    } else if (target.name === 'grid') {
      // For desktop, set the product-grid-view as usual
      // Update all radio buttons in the same group to ensure correct visual state
      this.#updateRadioButtonStates('grid', value);
      this.#animateLayoutChange(value);
    }
  }

  /**
   * Updates the checked state of all radio buttons in a group
   * @param {string} name - The name attribute of the radio group
   * @param {string} value - The value of the radio button to check
   */
  #updateRadioButtonStates(name, value) {
    const allInputs = document.querySelectorAll(`input[type="radio"][name="${name}"]`);
    allInputs.forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = input.value === value;
      }
    });
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = (value) => {
    const grid = this.#getGrid();
    if (!grid) return;

    const previousView = grid.getAttribute('product-grid-view');

    const controlName = mediaQueryLarge.matches ? 'grid' : 'grid-mobile';
    const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
    sessionStorage.setItem(`product-grid-view-${viewport}`, value);

    if (mediaQueryLarge.matches) {
      if (value === 'zoom-out') {
        // Apply dense layout immediately so the grid feels responsive,
        // then progressively fill remaining pages in the background.
        this.#setLayout('zoom-out');
        this.#updateRadioButtonStates('grid', 'zoom-out');
        void this.loadAllPagesForDenseView();
        return;
      }

      if (previousView === 'zoom-out') {
        // Remorph while still dense (banner placement), then switch layout and
        // re-bind cards — dense init only wires prefetch, not swatch/hover/nav.
        void this.restoreGridFromDenseView().then(() => {
          this.#setLayout(value);
          this.#updateRadioButtonStates('grid', value);
          document.dispatchEvent(
            new CustomEvent('pz-collection-cards:mount', {
              detail: { root: this, reason: 'dense-exit' },
            })
          );
          document.dispatchEvent(
            new CustomEvent('collection:grid-banners:place', {
              detail: { root: this, forceEnd: true, resetPlaced: true },
            })
          );
        });
        return;
      }
    }

    this.#setLayout(value);
    this.#updateRadioButtonStates(controlName, value);
  };

  /**
   * @returns {Promise<boolean>}
   */
  async loadAllPagesForDenseView() {
    return super.loadAllPagesForDenseView();
  }

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const grid = this.#getGrid();
    if (!grid) {
      return;
    }

    const currentDevice = mediaQueryLarge.matches ? 'desktop' : 'mobile';
    let layout = value;

    // Dense grid is desktop-only; never apply zoom-out on mobile
    if (currentDevice === 'mobile' && layout === 'zoom-out') {
      layout = 'default';
    }

    grid.setAttribute('product-grid-view', layout);

    if (currentDevice === 'desktop') {
      this.#updateRadioButtonStates('grid', layout);
    } else {
      this.#updateRadioButtonStates('grid-mobile', layout);
    }
  }

  /**
   * @returns {HTMLUListElement | null}
   */
  #getGrid() {
    const activeGrid = getActiveCollectionGrid(this);
    if (activeGrid instanceof HTMLUListElement) return activeGrid;
    const fallback = this.refs?.grid || this.querySelector('.product-grid');
    return fallback instanceof HTMLUListElement ? fallback : null;
  }

  /**
   * Clears desktop-only dense state when on mobile.
   */
  #sanitizeGridViewForViewport() {
    const grid = this.#getGrid();
    if (!grid) return;

    if (!mediaQueryLarge.matches) {
      if (grid.getAttribute('product-grid-view') === 'zoom-out') {
        const mobileLayout = sessionStorage.getItem('product-grid-view-mobile') || 'default';
        grid.setAttribute('product-grid-view', mobileLayout);
      }
      delete grid.dataset.denseAllLoaded;
    }
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = async (event) => {
    const grid = this.#getGrid();

    if (!event.matches && grid?.getAttribute('product-grid-view') === 'zoom-out') {
      await this.restoreGridFromDenseView();
      const mobileLayout = sessionStorage.getItem('product-grid-view-mobile') || 'default';
      this.#setLayout(mobileLayout);
      document.dispatchEvent(
        new CustomEvent('pz-collection-cards:mount', {
          detail: { root: this, reason: 'dense-exit' },
        })
      );
    }

    if (event.matches) {
      const desktopLayout = sessionStorage.getItem('product-grid-view-desktop') || 'default';
      const desktopDefault = document.querySelector('[data-grid-layout="desktop-default-option"]');
      const desktopDense = document.querySelector('input[type="radio"][name="grid"][value="zoom-out"]');
      const target = desktopLayout === 'zoom-out' && desktopDense instanceof HTMLInputElement ? desktopDense : desktopDefault;
      if (target instanceof HTMLInputElement) {
        target.checked = true;
      }
      this.#setLayout(desktopLayout);
      if (desktopLayout === 'zoom-out') {
        await this.loadAllPagesForDenseView();
      }
    } else {
      const mobileLayout = sessionStorage.getItem('product-grid-view-mobile') || 'default';
      const mobileTarget =
        mobileLayout === 'mobile-single'
          ? document.querySelector('input[type="radio"][name="grid-mobile"][value="mobile-single"]')
          : document.querySelector('[data-grid-layout="mobile-option"]');
      if (mobileTarget instanceof HTMLInputElement) {
        mobileTarget.checked = true;
      }
      this.#setLayout(mobileLayout);
    }
  };

  /**
   * Handles section load events to restore grid view after section morphs
   */
  #handleSectionLoad = (event) => {
    // Only handle if it's this section
    if (event.detail?.sectionId === this.sectionId) {
      // Restore grid view after a short delay to ensure DOM is ready
      setTimeout(() => {
        this.#restoreGridView();
      }, 100);
    }
  };

  /**
   * Attaches event listeners directly to grid controls
   */
  #attachGridControlListeners() {
    // Find all grid controls in the document (they may be outside results-list)
    const gridInputs = document.querySelectorAll('input[type="radio"][name="grid"], input[type="radio"][name="grid-mobile"]');
    gridInputs.forEach((input) => {
      if (input instanceof HTMLInputElement && !input.dataset.gridListenerAttached) {
        // Mark as having listener attached to avoid duplicates
        input.dataset.gridListenerAttached = 'true';
        
        // Add change listener
        input.addEventListener('change', (event) => {
          const grid = this.#getGrid();
          if (grid && event.target === input) {
            this.updateLayout(event);
          }
        });
      }
    });
  }

  /**
   * Watches for dynamically added grid controls and ensures they work
   */
  #watchForGridControls() {
    if (!window.MutationObserver) return;
    
    // Disconnect existing observer if any
    if (this.#gridControlsObserver) {
      this.#gridControlsObserver.disconnect();
    }
    
    this.#gridControlsObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            // Check if grid controls were added
            const gridInputs = node.matches?.('input[type="radio"][name="grid"], input[type="radio"][name="grid-mobile"]')
              ? [node]
              : node.querySelectorAll?.('input[type="radio"][name="grid"], input[type="radio"][name="grid-mobile"]') || [];
            
            // Re-attach listeners to new inputs
            if (gridInputs.length > 0) {
              this.#attachGridControlListeners();
            }
          }
        });
      });
    });
    
    const observeTarget =
      document.getElementById(this.sectionId) ||
      this.closest('.shopify-section') ||
      this;

    this.#gridControlsObserver.observe(observeTarget, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Restores the grid view from sessionStorage after section re-render
   */
  #restoreGridView() {
    requestAnimationFrame(() => {
      this.#sanitizeGridViewForViewport();
      const grid = this.#getGrid();
      if (!grid) return;

      const currentDevice = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      const storedLayout = sessionStorage.getItem(`product-grid-view-${currentDevice}`);

      if (storedLayout) {
        const layout =
          currentDevice === 'desktop'
            ? storedLayout
            : storedLayout === 'zoom-out'
              ? 'default'
              : storedLayout;

        grid.setAttribute('product-grid-view', layout);

        if (currentDevice === 'desktop') {
          const desktopOptions = document.querySelectorAll('input[type="radio"][name="grid"]');
          desktopOptions.forEach((option) => {
            if (option instanceof HTMLInputElement) {
              option.checked = option.value === storedLayout;
            }
          });

          if (storedLayout === 'zoom-out') {
            this.loadAllPagesForDenseView();
          }
        } else {
          const mobileOptions = document.querySelectorAll('input[type="radio"][name="grid-mobile"]');
          mobileOptions.forEach((option) => {
            if (option instanceof HTMLInputElement) {
              option.checked = option.value === layout;
            }
          });
        }
      }
    });
  }
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
